import { Maybe } from 'maybe-not';
import promiseFromEvent from '../utility/promiseFromEvent';
import { isIE } from '../utility/user-agent';
import { TectonResolvedMenuItem } from '../types/index';
import IDict from 'q2-tecton-common/lib/types/dict';

interface ElementScript extends HTMLScriptElement {
    loaded?: boolean;
}

type LocFn = (key: string, substitutions?: string[]) => string;
type ResolveMenuFn = (
    outletId: string,
    contextValue?: string,
    resolvedType?: string
) => Promise<TectonResolvedMenuItem[]>;
export type NavigateToFn = (featureName: string, moduleName?: string) => void;
export type ShowOverpanelFn = (
    overpanelPath: string,
    params?: IDict<string>,
    options?: { fullWidth?: boolean }
) => void;
export type TectonElementWindow = typeof window & {
    TectonElements: {
        loc: LocFn;
        resizeIframe: () => void;
        resolveMenu: ResolveMenuFn;
        navigateTo: NavigateToFn;
        showOverpanel: ShowOverpanelFn;
    };
};

export default function setupQ2Elements(
    loc: LocFn,
    resizeIframe: () => void,
    resolveMenu: Maybe<ResolveMenuFn>,
    navigateTo: Maybe<NavigateToFn>,
    showOverpanel: Maybe<ShowOverpanelFn>,
    teardowns: (() => void)[],
    cdnOverridePromise: Promise<Maybe<string>>
): Promise<void> {
    (window as TectonElementWindow).TectonElements = {
        ...(window as TectonElementWindow).TectonElements,
        loc,
        resizeIframe
    };

    resolveMenu.map(fn => ((window as TectonElementWindow).TectonElements.resolveMenu = fn));
    navigateTo.map(fn => ((window as TectonElementWindow).TectonElements.navigateTo = fn));
    showOverpanel.map(fn => ((window as TectonElementWindow).TectonElements.showOverpanel = fn));

    return cdnOverridePromise.then(maybeCdnUrl => {
        const src = getScriptURL(maybeCdnUrl);
        const hasScript = !!document.querySelector(`script.q2-tecton-elements`);

        if (hasScript) {
            return Promise.resolve();
        }

        if (!window.Element.prototype.attachShadow) {
            remapInsertBefore();
            remapRemoveChild();
        }

        const moduleScriptSrc = `${src}.esm.js`;
        const nomoduleScriptSrc = `${src}.js`;
        const moduleScriptTag = document.createElement('script');
        moduleScriptTag.src = moduleScriptSrc;
        moduleScriptTag.type = 'module';
        moduleScriptTag.classList.add('q2-tecton-elements');

        const nomoduleScriptTag = document.createElement('script');
        nomoduleScriptTag.src = nomoduleScriptSrc;
        nomoduleScriptTag.setAttribute('nomodule', '');
        nomoduleScriptTag.classList.add('q2-tecton-elements');

        document.body.appendChild(moduleScriptTag);
        document.body.appendChild(nomoduleScriptTag);

        teardowns.push(() => {
            document.body.removeChild(moduleScriptTag);
            document.body.removeChild(nomoduleScriptTag);
        });

        const script = document.querySelector<HTMLScriptElement>(
            `script[src="${isIE ? nomoduleScriptSrc : moduleScriptSrc}"]`
        )!;

        return promiseFromEvent(script, 'load').then(function() {
            (script as ElementScript).loaded = true;
        });
    });
}

function getScriptURL(cdnOverrideURL: Maybe<string>): string {
    const fileName = `q2-tecton-elements${isIE ? '-ie' : ''}`;
    const mode = TECTON_SDK_BUILD_MODE;
    let baseURLPath = `${cdnOverrideURL.withDefault(
        'https://cdn1.onlineaccess1.com/cdn/'
    )}base/tecton/v${TECTONSDKVERSION}/${fileName}`;

    if (mode === 'none') {
        baseURLPath = `http://localhost:${isIE ? '3335' : '3334'}/dist`;
    } else if (mode === 'development') {
        const url = new URL(fileName, window.location.href);
        // "uitest" is a special branch only for design review purpose
        const branch = url.pathname.endsWith(`/uitest/${fileName}`) ? 'uitest' : 'develop';
        baseURLPath = `http://devcdn/cdn/base/tecton/${branch}/${fileName}`;
    }

    const filePath = `${baseURLPath}/${fileName}/${fileName}`;
    return filePath;
}

function remapInsertBefore(): void {
    const nativeInsertBefore = window.Node.prototype.insertBefore;
    function wrappedInsertBefore<T extends Node>(this: any, newChild: T, refChild: Node | null): T {
        /* tslint:disable:no-this-assignment */
        let referenceNode = this;
        /* tslint:enable:no-this-assignment */
        if (refChild && refChild.parentNode && referenceNode !== refChild.parentNode) {
            referenceNode = refChild.parentNode;
        }
        return nativeInsertBefore.call(referenceNode, newChild, refChild) as T;
    }

    window.Node.prototype.insertBefore = wrappedInsertBefore;
}

function remapRemoveChild(): void {
    const nativeRemoveChild = window.Node.prototype.removeChild;
    function wrappedRemoveChild<T extends Node>(this: any, oldChild: T): T {
        /* tslint:disable:no-this-assignment */
        let referenceNode = this;
        /* tslint:enable:no-this-assignment */
        if (oldChild.parentNode && referenceNode !== oldChild.parentNode) {
            referenceNode = oldChild.parentNode;
        }
        return nativeRemoveChild.call(referenceNode, oldChild) as T;
    }

    window.Node.prototype.removeChild = wrappedRemoveChild;
}
